import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material';
import {
  PlayArrow as CalculateIcon,
  Visibility as PreviewIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Accessibility as AccessibilityIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getMOEIndicatorTemplates,
  calculateMOEIndicator,
  exportMOEIndicatorResults,
  formatIndicatorValue,
  validateMOECalculationRequest,
  groupTemplatesByCategory,
  getCategoryColor,
  getCategoryLabel,
  getEducationLevelLabel,
  getOwnershipLabel,
  getUnitLabel,
  EDUCATION_LEVELS,
  OWNERSHIP_TYPES,
  INDICATOR_CATEGORIES,
  type MOEIndicatorTemplate,
  type MOEIndicatorResult,
  type MOECalculationRequest,
  type MOECalculationResponse,
  type CategoryMeta,
} from '../../../services/moeIndicatorsApi';

const GROUPING_OPTIONS = [
  { value: 'district', label: 'By District' },
  { value: 'region', label: 'By Region' },
  { value: 'national', label: 'National Total' },
];

const DISTRICTS = [
  'Chake Chake', 'Kaskazini A', 'Kaskazini B', 'Kati', 'Kusini',
  'Magharibi A', 'Magharibi B', 'Micheweni', 'Mjini', 'Mkoani', 'Wete'
];

const REGIONS = ['Zanzibar North', 'Zanzibar South', 'Zanzibar Central', 'Pemba North', 'Pemba South'];

const ACADEMIC_YEARS = [
  { value: '2026', label: '2025/2026' },
  { value: '2025', label: '2024/2025' },
  { value: '2024', label: '2023/2024' },
  { value: '2023', label: '2022/2023' },
  { value: '2022', label: '2021/2022' },
];

// Map category values to MUI icons
const CATEGORY_ICONS: Record<string, React.ReactElement> = {
  school_count: <SchoolIcon />,
  enrollment: <PeopleIcon />,
  efficiency: <TrendingUpIcon />,
  infrastructure: <BusinessIcon />,
  staff: <PersonIcon />,
  equity: <AccessibilityIcon />,
  academic: <AssessmentIcon />,
};

const MOEIndicatorBuilder: React.FC = () => {
  // Calculation parameters
  const [academicYear, setAcademicYear] = useState<string>('2026');
  const [groupBy, setGroupBy] = useState<string>('district');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [educationLevel, setEducationLevel] = useState<string>('all');
  const [ownership, setOwnership] = useState<string>('all');

  // Indicator selection
  const [selectedIndicator, setSelectedIndicator] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Results
  const [calculationResults, setCalculationResults] = useState<MOEIndicatorResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<MOEIndicatorResult[]>([]);
  const [lastResponse, setLastResponse] = useState<MOECalculationResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templates, setTemplates] = useState<MOEIndicatorTemplate[]>([]);

  // Expanded accordion categories (all open by default)
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  // Fetch MOE indicator templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['moe-templates'],
    queryFn: getMOEIndicatorTemplates,
  });

  // Update templates when data is loaded
  React.useEffect(() => {
    if (templatesData?.data) {
      setTemplates(templatesData.data);
      // Default all categories to expanded
      const categories = templatesData.meta?.categories || INDICATOR_CATEGORIES;
      const expanded: Record<string, boolean> = {};
      categories.forEach((cat: CategoryMeta) => { expanded[cat.value] = true; });
      setExpandedCategories(expanded);
    }
  }, [templatesData]);

  // Mutation for calculating indicators
  const calculateMutation = useMutation({
    mutationFn: calculateMOEIndicator,
    onSuccess: (data) => {
      const results = data.data.results || [];
      setCalculationResults(results);
      setFilteredResults(results);
      setLastResponse(data);
      setDialogOpen(true);
    },
    onError: (error: any) => {
      console.error('Calculation failed:', error);
      alert(`Calculation failed: ${error.message || 'Unknown error'}`);
    },
  });

  // Filter results based on selected area
  React.useEffect(() => {
    if (!selectedArea) {
      setFilteredResults(calculationResults);
    } else {
      const filtered = calculationResults.filter(result =>
        result.area_name.toLowerCase().includes(selectedArea.toLowerCase())
      );
      setFilteredResults(filtered);
    }
  }, [selectedArea, calculationResults]);

  // Get available areas based on grouping level
  const getAvailableAreas = () => {
    if (groupBy === 'district') return DISTRICTS;
    if (groupBy === 'region') return REGIONS;
    return ['Zanzibar'];
  };

  // Group and filter templates
  const groupedTemplates = useMemo(() => {
    let filtered = templates;

    // Filter by category if set
    if (categoryFilter && categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    return groupTemplatesByCategory(filtered);
  }, [templates, categoryFilter]);

  // Category order from backend meta or fallback
  const categoryOrder = useMemo(() => {
    return (templatesData?.meta?.categories || INDICATOR_CATEGORIES).map((c: CategoryMeta) => c.value);
  }, [templatesData]);

  // Get total indicator count per category
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    templates.forEach(t => {
      counts[t.category] = (counts[t.category] || 0) + 1;
    });
    return counts;
  }, [templates]);

  const handleCalculate = async () => {
    if (!selectedIndicator) return;

    const selectedTemplate = templates.find(t => t.code === selectedIndicator);

    const request: MOECalculationRequest = {
      indicator_code: selectedIndicator,
      academic_year: academicYear,
      group_by: groupBy as 'district' | 'region' | 'national',
      education_level: (selectedTemplate?.supports_level ? educationLevel : selectedTemplate?.default_level || 'all') as any,
      ownership: (selectedTemplate?.supports_ownership ? ownership : selectedTemplate?.default_ownership || 'all') as any,
    };

    // Validate request
    const errors = validateMOECalculationRequest(request);
    if (errors.length > 0) {
      alert(`Validation errors:\n${errors.join('\n')}`);
      return;
    }

    calculateMutation.mutate(request);
  };

  const handleExport = () => {
    if (filteredResults.length === 0) return;

    const selectedTemplate = templates.find(t => t.code === selectedIndicator);
    const indicatorName = selectedTemplate?.name || '';

    try {
      exportMOEIndicatorResults(
        filteredResults,
        selectedIndicator,
        indicatorName,
        academicYear,
        lastResponse?.data?.education_level_label,
        lastResponse?.data?.ownership_label
      );
    } catch (error: any) {
      alert(`Export failed: ${error.message}`);
    }
  };

  const getTotalValue = () => {
    return filteredResults.reduce((sum, result) => sum + result.indicator_value, 0);
  };

  const getAverageValue = () => {
    if (filteredResults.length === 0) return 0;
    return getTotalValue() / filteredResults.length;
  };

  const selectedTemplate = templates.find(t => t.code === selectedIndicator);

  const handleAccordionToggle = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        MOE Standard Indicators
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
        Calculate official Ministry of Education indicators using live SIS data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {templates.length} indicators across {Object.keys(categoryCounts).length} categories
        &mdash; supports all education levels and ownership types
      </Typography>

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* Calculation Parameters                                          */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon color="primary" /> Calculation Parameters
          </Typography>
          <Grid container spacing={2}>
            {/* Row 1: Year, Level, Ownership */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Academic Year"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                size="small"
              >
                {ACADEMIC_YEARS.map(year => (
                  <MenuItem key={year.value} value={year.value}>
                    {year.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Education Level"
                value={educationLevel}
                onChange={(e) => setEducationLevel(e.target.value)}
                size="small"
              >
                {EDUCATION_LEVELS.map(level => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Ownership"
                value={ownership}
                onChange={(e) => setOwnership(e.target.value)}
                size="small"
              >
                {OWNERSHIP_TYPES.map(own => (
                  <MenuItem key={own.value} value={own.value}>
                    {own.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Row 2: Grouping, Area filter */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Grouping Level"
                value={groupBy}
                onChange={(e) => {
                  setGroupBy(e.target.value);
                  setSelectedArea('');
                }}
                size="small"
              >
                {GROUPING_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Filter by Area (Optional)"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
                disabled={groupBy === 'national'}
                size="small"
              >
                <MenuItem value="">All Areas</MenuItem>
                {getAvailableAreas().map(area => (
                  <MenuItem key={area} value={area}>
                    {area}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Filter by Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {(templatesData?.meta?.categories || INDICATOR_CATEGORIES).map((cat: CategoryMeta) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label} ({categoryCounts[cat.value] || 0})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* Indicator Templates — Grouped by Category                       */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Typography variant="h6" gutterBottom>
        Select an Indicator to Calculate
      </Typography>

      {templatesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {categoryOrder
            .filter((catValue: string) => groupedTemplates[catValue] && groupedTemplates[catValue].length > 0)
            .map((catValue: string) => {
              const catMeta = (templatesData?.meta?.categories || INDICATOR_CATEGORIES)
                .find((c: CategoryMeta) => c.value === catValue);
              const catTemplates = groupedTemplates[catValue] || [];
              const catColor = catMeta?.color || '#666';
              const catLabel = catMeta?.label || catValue;
              const catIcon = CATEGORY_ICONS[catValue] || <AssessmentIcon />;

              return (
                <Accordion
                  key={catValue}
                  expanded={expandedCategories[catValue] ?? true}
                  onChange={() => handleAccordionToggle(catValue)}
                  sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    borderLeft: `4px solid ${catColor}`,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: 'grey.50' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <Box sx={{ color: catColor }}>{catIcon}</Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {catLabel}
                      </Typography>
                      <Badge
                        badgeContent={catTemplates.length}
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 2 }}>
                    <Grid container spacing={2}>
                      {catTemplates.map((template) => (
                        <Grid item xs={12} sm={6} md={4} key={template.code}>
                          <Card
                            sx={{
                              height: '100%',
                              cursor: 'pointer',
                              border: selectedIndicator === template.code ? 2 : 1,
                              borderColor: selectedIndicator === template.code ? catColor : 'divider',
                              backgroundColor: selectedIndicator === template.code ? `${catColor}08` : 'background.paper',
                              '&:hover': {
                                boxShadow: 3,
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                            onClick={() => setSelectedIndicator(template.code)}
                          >
                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                  {template.code}
                                </Typography>
                                <Chip
                                  label={template.unit}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              </Box>
                              <Typography variant="body2" fontWeight="bold" gutterBottom>
                                {template.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                {template.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {template.supports_level && (
                                  <Chip
                                    label="Level"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                                {template.supports_ownership && (
                                  <Chip
                                    label="Ownership"
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                                <Chip
                                  label={template.default_level_label || template.default_level}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem', color: 'text.secondary' }}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </Box>
      )}

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* Action Buttons                                                   */}
      {/* ──────────────────────────────────────────────────────────────── */}
      {selectedIndicator && (
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={calculateMutation.isPending ? <CircularProgress size={20} /> : <CalculateIcon />}
            onClick={handleCalculate}
            disabled={calculateMutation.isPending}
          >
            {calculateMutation.isPending ? 'Calculating...' : 'Calculate Indicator'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => {
              const tpl = templates.find(t => t.code === selectedIndicator);
              if (tpl) {
                alert(
                  `Indicator: ${tpl.code} - ${tpl.name}\n` +
                  `Query Type: ${tpl.query_type}\n` +
                  `Education Level: ${educationLevel} (${getEducationLevelLabel(educationLevel)})\n` +
                  `Ownership: ${ownership} (${getOwnershipLabel(ownership)})\n` +
                  `Group By: ${groupBy}\n` +
                  `Year: ${academicYear}\n` +
                  `Unit: ${tpl.unit}\n` +
                  `Data Type: ${tpl.data_type}`
                );
              }
            }}
          >
            Preview Query
          </Button>
        </Box>
      )}

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* Selected Indicator Info                                          */}
      {/* ──────────────────────────────────────────────────────────────── */}
      {selectedTemplate && (
        <Alert
          severity="info"
          sx={{ mt: 3 }}
          icon={CATEGORY_ICONS[selectedTemplate.category] || <AssessmentIcon />}
        >
          <AlertTitle>
            Selected: {selectedTemplate.code} &mdash; {selectedTemplate.name}
          </AlertTitle>
          {selectedTemplate.description}<br />
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={getCategoryLabel(selectedTemplate.category)}
              size="small"
              sx={{ backgroundColor: getCategoryColor(selectedTemplate.category), color: 'white' }}
            />
            <Chip
              label={`Level: ${selectedTemplate.supports_level ? getEducationLevelLabel(educationLevel) : getEducationLevelLabel(selectedTemplate.default_level)}`}
              size="small"
              color="info"
              variant="outlined"
            />
            <Chip
              label={`Ownership: ${selectedTemplate.supports_ownership ? getOwnershipLabel(ownership) : getOwnershipLabel(selectedTemplate.default_ownership)}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={GROUPING_OPTIONS.find(g => g.value === groupBy)?.label || groupBy}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Year: ${academicYear}`}
              size="small"
              variant="outlined"
            />
          </Box>
          {!selectedTemplate.supports_level && educationLevel !== 'all' && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
              Note: This indicator does not support education level filtering. Default level ({selectedTemplate.default_level_label}) will be used.
            </Typography>
          )}
          {!selectedTemplate.supports_ownership && ownership !== 'all' && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
              Note: This indicator does not support ownership filtering. Default ownership ({selectedTemplate.default_ownership_label}) will be used.
            </Typography>
          )}
        </Alert>
      )}

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* Results Dialog                                                   */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                {lastResponse?.data?.indicator_code} &mdash; {lastResponse?.data?.indicator_name || selectedTemplate?.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                {lastResponse?.data?.category && (
                  <Chip
                    label={getCategoryLabel(lastResponse.data.category)}
                    size="small"
                    sx={{ backgroundColor: getCategoryColor(lastResponse.data.category), color: 'white', height: 22 }}
                  />
                )}
                {lastResponse?.data?.education_level_label && (
                  <Chip label={lastResponse.data.education_level_label} size="small" color="info" variant="outlined" sx={{ height: 22 }} />
                )}
                {lastResponse?.data?.ownership_label && (
                  <Chip label={lastResponse.data.ownership_label} size="small" color="secondary" variant="outlined" sx={{ height: 22 }} />
                )}
                {lastResponse?.data?.unit && (
                  <Chip label={getUnitLabel(lastResponse.data.unit)} size="small" variant="outlined" sx={{ height: 22 }} />
                )}
              </Box>
            </Box>
            <Box>
              <Tooltip title="Export to CSV">
                <IconButton onClick={handleExport} disabled={calculationResults.length === 0}>
                  <ExportIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Recalculate">
                <IconButton onClick={handleCalculate}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {filteredResults.length > 0 ? (
            <>
              {/* Summary stats */}
              <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      {lastResponse?.data?.data_type === 'decimal' && lastResponse?.data?.unit === 'percentage'
                        ? `${getAverageValue().toFixed(1)}% avg`
                        : lastResponse?.data?.data_type === 'decimal' && lastResponse?.data?.unit === 'ratio'
                          ? `${getAverageValue().toFixed(2)} avg`
                          : getTotalValue().toLocaleString()
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Academic Year</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {lastResponse?.data?.academic_year || academicYear}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Results</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {filteredResults.length} of {calculationResults.length} areas
                    </Typography>
                  </Grid>
                </Grid>
                {selectedArea && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Filtered by: {selectedArea}
                  </Typography>
                )}
              </Box>

              {/* Results table */}
              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Area</strong></TableCell>
                      <TableCell align="right"><strong>Value</strong></TableCell>
                      <TableCell><strong>Unit</strong></TableCell>
                      <TableCell><strong>Period</strong></TableCell>
                      <TableCell><strong>Source</strong></TableCell>
                      <TableCell><strong>Year</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredResults.map((result, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{result.area_name}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {lastResponse?.data
                              ? formatIndicatorValue(result.indicator_value, lastResponse.data.unit, lastResponse.data.data_type)
                              : result.indicator_value.toLocaleString()
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getUnitLabel(lastResponse?.data?.unit || selectedTemplate?.unit || '')}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>{result.period}</TableCell>
                        <TableCell>
                          <Chip label={result.source} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell>{result.year}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : calculationResults.length > 0 ? (
            <Alert severity="warning">
              No results match your current filter. Try selecting a different area or clear the filter.
            </Alert>
          ) : (
            <Alert severity="info">
              No results to display. Click "Calculate Indicator" to generate data.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleExport} disabled={filteredResults.length === 0} startIcon={<ExportIcon />}>
            Export Results
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MOEIndicatorBuilder;
